import React, { useMemo } from 'react';
import { Button, Tooltip, Box } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { type ComponentDetails, type HealthScore } from 'types/squares.type';
import { PNG } from 'components/Asset/PNG';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import { RiskStatBlockWithTooltip } from './RiskStatBlockWithTooltip';
import { type ServiceView } from 'types/survivalAnalysis.types';
import { convertAssetHealthScoreDataToServiceView } from '../../utils/dataFormatters';
import { healthScorePieRange } from '../../../DashboardV2/constants/squares.dashboard.constants';
import HealthScorePie from '../../../DashboardV2/Helpers/HealthScore.Pie';
import { useSearchParams } from 'react-router-dom';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME } from 'utils/clientTheme';

function bucketizeComponents(components: ComponentDetails[]) {
  const buckets: Record<'high' | 'med' | 'low', ComponentDetails[]> = {
    high: [],
    med: [],
    low: [],
  };
  components.forEach((component) => {
    const category = component.work_order_age_category?.toLowerCase();
    if (category === 'high') buckets.high.push(component);
    else if (category === 'med') buckets.med.push(component);
    else if (category === 'low') buckets.low.push(component);
  });

  return buckets;
}

const AssetHeader: React.FC<{
  assetDetail: HealthScore['asset_details'];
  enableDarkTheme: boolean;
  titleColor: string;
  labelColor: string;
}> = ({ assetDetail, enableDarkTheme, titleColor, labelColor }) => {
  const thumbnailBackground =
    CLIENT_THEME === 'yokohama'
      ? 'transparent'
      : enableDarkTheme
      ? '#1C3459'
      : '#EEEEEE';
  const thumbnailFrameBackground =
    CLIENT_THEME === 'yokohama'
      ? 'transparent'
      : enableDarkTheme
      ? 'linear-gradient(135deg, #475569, #64748b)'
      : 'linear-gradient(135deg, #e2e8f0, #cbd5e1)';

  return (
    <div
      style={{
        padding: '8px 16px',
        background: enableDarkTheme
          ? 'rgba(21, 38, 66, 0.95)'
          : 'rgba(248, 250, 252, 0.8)',
        borderBottom: enableDarkTheme
          ? '1px solid rgba(148, 163, 184, 0.1)'
          : '1px solid rgba(226, 232, 240, 0.5)',
        display: 'flex',
        gap: '12px',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '12px',
        }}
      >
        <div
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: thumbnailFrameBackground,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 600,
            color: enableDarkTheme ? '#f1f5f9' : '#475569',
          }}
        >
          <Box
            width="70px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              height="40px"
              width="40px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                position: 'relative',
                borderRadius: '50%',
                background: thumbnailBackground,
                zIndex: 1,
              }}
            >
              <Box
                component={PNG}
                name={getImageNameByMakeModelAndAsset(
                  assetDetail?.make,
                  assetDetail?.model,
                  assetDetail?.asset_type,
                  'Small'
                )}
                alt={assetDetail?.bumper_number}
                sx={{
                  position: 'absolute',
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                  zIndex: 2,
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              />
            </Box>
          </Box>
        </div>

        <div
          style={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            justifyContent: 'center',
            flex: 1,
          }}
        >
          <Tooltip title={assetDetail?.bumper_number ?? ''} arrow>
            <h3
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: titleColor,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                margin: 0,
                letterSpacing: '-0.025em',
              }}
            >
              {assetDetail?.bumper_number}
            </h3>
          </Tooltip>
          <p
            style={{
              fontSize: '12px',
              color: labelColor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              margin: 0,
              fontWeight: 500,
            }}
            title={`${assetDetail?.make}-${assetDetail?.model}`}
          >
            {assetDetail?.make} • {assetDetail?.model}
          </p>
          <p
            style={{
              fontSize: '12px',
              color: labelColor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              margin: 0,
              fontWeight: 500,
              textTransform: 'capitalize',
            }}
          >
            {assetDetail?.asset_type?.split('_')?.join(' ')}
          </p>
        </div>
      </div>
      <p
        style={{
          fontSize: '12px',
          color: labelColor,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          margin: 0,
          fontWeight: 500,
        }}
      >
        Last maintenance:{' '}
        {convertStdDateString(
          new Date().setDate(
            new Date().getDate() - Number(assetDetail.age_in_days)
          )
        )}
      </p>
    </div>
  );
};

const AssetCard: React.FC<{
  asset: HealthScore;
  handleShowAssetDetails: (
    value: boolean | null,
    paramSelectedAsset?: ServiceView,
    selectedView?: string
  ) => void;
}> = ({ asset, handleShowAssetDetails }) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const [, setSearchParams] = useSearchParams();
  const { asset_details: assetDetail, component_details: componentDetails } =
    asset;

  const riskBuckets = useMemo(
    () => bucketizeComponents(componentDetails),
    [componentDetails]
  );

  // stylings
  const cardStyle = {
    borderRadius: '6px',
    boxShadow: enableDarkTheme
      ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)'
      : '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 16px rgba(0, 0, 0, 0.04)',
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    background: enableDarkTheme
      ? 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)'
      : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
    border: enableDarkTheme
      ? '1px solid rgba(148, 163, 184, 0.1)'
      : '1px solid rgba(226, 232, 240, 0.6)',
    transition: 'all 0.3s ease',
    position: 'relative' as const,
    gap: '12px',
  };

  const titleColor = enableDarkTheme ? '#f1f5f9' : '#0f172a';
  const labelColor = enableDarkTheme ? '#94a3b8' : '#64748b';

  const handleAssetViewDetailsClick = () => {
    const tempServiceView = convertAssetHealthScoreDataToServiceView(
      asset.asset_details
    );
    handleShowAssetDetails(true, tempServiceView);
    setSearchParams({ view: 'all_asset' }, { replace: true });
  };
  return (
    <div style={cardStyle}>
      <AssetHeader
        assetDetail={assetDetail}
        enableDarkTheme={enableDarkTheme}
        titleColor={titleColor}
        labelColor={labelColor}
      />

      <div
        style={{
          padding: '0px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          flexGrow: 1,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <p
            style={{
              fontSize: '12px',
              color: labelColor,
              fontWeight: 500,
              margin: 0,
            }}
          >
            {t('asset_health')}
          </p>
          <HealthScorePie
            enableDarkTheme={enableDarkTheme}
            data={healthScorePieRange}
            value={Number(assetDetail?.health_score) * 100}
            width={140}
            height={90}
            innerRadius={30}
            outerRadius={40}
            needleColor="#d0d000"
            label={`${(Number(assetDetail?.health_score) * 100).toFixed(0)}%`}
            arrowDirection={false}
            showArrowDirection={false}
          />

          <p
            style={{
              fontSize: '12px',
              color: labelColor,
              fontWeight: 500,
              margin: 0,
            }}
          >
            {t('components')}
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '8px',
            width: '100%',
          }}
        >
          <RiskStatBlockWithTooltip
            value={riskBuckets.high.length}
            label={t('high')}
            type="high"
            components={riskBuckets.high}
          />
          <RiskStatBlockWithTooltip
            value={riskBuckets.med.length}
            label={t('med')}
            type="med"
            components={riskBuckets.med}
          />
          <RiskStatBlockWithTooltip
            value={riskBuckets.low.length}
            label={t('low')}
            type="low"
            components={riskBuckets.low}
          />
        </div>
      </div>

      <div
        style={{
          padding: '0 16px 16px',
          marginTop: 'auto',
        }}
      >
        <Button
          size="small"
          sx={{
            width: '100%',
            height: '100%',
            border: '1px solid #2B53BC',
            borderRadius: '5px',
            py: '4px',
            textTransform: 'none',
          }}
          color={enableDarkTheme ? 'primary' : 'info'}
          onClick={handleAssetViewDetailsClick}
          variant="contained"
        >
          {t('view_details_2')}
        </Button>
      </div>
    </div>
  );
};

export default AssetCard;
